import type { CrossingKind, MapDocument, MapPlace, Seam, SeamCrossing, SeamState, WorldDocument } from "./types.ts";

/**
 * The world's layout (ADR-109 decisions 1 to 7, ADR-122 decision 1): where each map sits on the world grid or
 * off it, the map groups and the seams between placed maps, the geometry of places, the neighbours a place gives
 * (`neighboursOf`) and a crossing's span as either map numbers it (`crossingSpanFor`). The rules a layout keeps
 * are `layout-rules.ts`'s. Everything here is pure and names nothing of any game.
 */

/**
 * The furthest a map's place may stand from the world's origin along either axis, in tiles (ADR-106 decision
 * 4's range, so world distances stay exact).
 */
export const PLACE_LIMIT = 500_000;

/**
 * The deepest a map may sit below the surface (ADR-122 decision 1.2).
 */
export const DEPTH_LOWEST = -8;

/**
 * The most characters an open copy of a map may hold (ADR-122 decision 4.2).
 */
export const CAPACITY_MOST = 600;

/**
 * The longest side of a room, in tiles (ADR-122 decision 1.2).
 */
export const ROOM_SIDE_MOST = 256;

/**
 * Every state a seam may hold, in the order the reader names them.
 */
export const SEAM_STATES: readonly SeamState[] = ["open", "closed", "closed-but"];

/**
 * Every kind a crossing may be, in the order the reader names them.
 */
export const CROSSING_KINDS: readonly CrossingKind[] = ["walk", "water", "walk-in"];

/**
 * A side of a map.
 */
export type MapSide = "north" | "east" | "south" | "west";

/**
 * A map that shares a stretch of edge with another: its id, the side of the other map it lies on, and the
 * stretch as inclusive corner numbers of that side, counted from 0 at its west end (north and south) or its
 * north end (east and west).
 */
export interface Neighbour {
  readonly map: string;
  readonly side: MapSide;
  readonly from: number;
  readonly to: number;
}

/**
 * A crossing's span as one map of its seam numbers it: the side of that map it is on, and inclusive corner
 * numbers of that side.
 */
export interface CrossingSpan {
  readonly side: MapSide;
  readonly from: number;
  readonly to: number;
}

const SIDE_ORDER: readonly MapSide[] = ["north", "east", "south", "west"];

/**
 * A seam's key and its name in messages: its pair, `a/b`.
 *
 * @param seam The seam, or its pair.
 * @returns The key.
 */
export function seamKey(seam: { readonly a: string; readonly b: string }): string {
  return `${seam.a}/${seam.b}`;
}

/**
 * Compares two strings by code unit, the order seams and crossings are kept in.
 *
 * @param x The first.
 * @param y The second.
 * @returns Negative, zero or positive.
 */
function byText(x: string, y: string): number {
  return x < y ? -1 : x > y ? 1 : 0;
}

/**
 * Seams in key order, so equal layouts serialise identically whatever order they were made in.
 *
 * @param seams The seams.
 * @returns A new list in key order.
 */
export function sortedSeams(seams: readonly Seam[]): Seam[] {
  return [...seams].sort((x, y) => byText(x.a, y.a) || byText(x.b, y.b));
}

/**
 * Crossings in name order.
 *
 * @param crossings The crossings.
 * @returns A new list in name order.
 */
export function sortedCrossings(crossings: readonly SeamCrossing[]): SeamCrossing[] {
  return [...crossings].sort((x, y) => byText(x.name, y.name));
}

/**
 * A world with its seams set: in key order, and the field dropped when none is left.
 *
 * @param world The world.
 * @param seams The seams.
 * @returns The new world.
 */
export function withSeams(world: WorldDocument, seams: readonly Seam[]): WorldDocument {
  const { seams: _previous, ...rest } = world;
  return seams.length === 0 ? rest : { ...rest, seams: sortedSeams(seams) };
}

/**
 * A map's depth: its place's, 0 for the surface or a map with no place.
 *
 * @param map The map.
 * @returns The depth.
 */
export function depthOf(map: MapDocument): number {
  return map.place?.depth ?? 0;
}

/**
 * A place in its saved form: the depth kept only below the surface.
 *
 * @param place The place, or undefined.
 * @returns The place without a depth of 0, or undefined.
 */
export function canonicalPlace(place: MapPlace | undefined): MapPlace | undefined {
  if (place === undefined) {
    return undefined;
  }
  return place.depth === undefined || place.depth === 0 ? { x: place.x, y: place.y } : { x: place.x, y: place.y, depth: place.depth };
}

/**
 * The rectangle a placed map covers on the world grid.
 *
 * @param map The map.
 * @returns Its rectangle, or null for a map with no place.
 */
export function rectOf(map: MapDocument): { readonly x: number; readonly y: number; readonly w: number; readonly h: number } | null {
  return map.place === undefined ? null : { x: map.place.x, y: map.place.y, w: map.width, h: map.height };
}

/**
 * Where map `b` touches map `a` along an edge: the side of `a` it lies on and the shared stretch in world
 * tiles along that side.
 *
 * @param a The first map.
 * @param b The second map.
 * @returns The side of `a` and the stretch, or null when either has no place or they share no stretch of
 *   edge (a gap, an overlap or a single corner).
 */
export function touching(a: MapDocument, b: MapDocument): { readonly side: MapSide; readonly start: number; readonly end: number } | null {
  const ra = rectOf(a);
  const rb = rectOf(b);
  if (ra === null || rb === null) {
    return null;
  }
  const xStart = Math.max(ra.x, rb.x);
  const xEnd = Math.min(ra.x + ra.w, rb.x + rb.w);
  const yStart = Math.max(ra.y, rb.y);
  const yEnd = Math.min(ra.y + ra.h, rb.y + rb.h);
  if (xEnd > xStart) {
    if (rb.y === ra.y + ra.h) {
      return { side: "south", start: xStart, end: xEnd };
    }
    if (rb.y + rb.h === ra.y) {
      return { side: "north", start: xStart, end: xEnd };
    }
  }
  if (yEnd > yStart) {
    if (rb.x === ra.x + ra.w) {
      return { side: "east", start: yStart, end: yEnd };
    }
    if (rb.x + rb.w === ra.x) {
      return { side: "west", start: yStart, end: yEnd };
    }
  }
  return null;
}

/**
 * Where a side of a map starts in world tiles along that side: its place's x for north and south, y for east
 * and west.
 *
 * @param place The map's place.
 * @param side The side.
 * @returns The start.
 */
function sideStart(place: MapPlace, side: MapSide): number {
  return side === "north" || side === "south" ? place.x : place.y;
}

/**
 * The maps that share a stretch of edge with a map, from the places alone, within the map's own depth (ADR-122
 * decision 8: between depths there are only doors). A gap, a single corner and a map with no place give none.
 *
 * @param world The world.
 * @param mapId The map.
 * @returns Its neighbours, by side (north, east, south, west) and then along the side.
 */
export function neighboursOf(world: WorldDocument, mapId: string): Neighbour[] {
  const map = world.maps.find((m) => m.id === mapId);
  if (map?.place === undefined) {
    return [];
  }
  const place = map.place;
  const found: Neighbour[] = [];
  for (const other of world.maps) {
    if (other.id === mapId || other.place === undefined || depthOf(other) !== depthOf(map)) {
      continue;
    }
    const touch = touching(map, other);
    if (touch !== null) {
      const origin = sideStart(place, touch.side);
      found.push({ map: other.id, side: touch.side, from: touch.start - origin, to: touch.end - origin });
    }
  }
  return found.sort((x, y) => SIDE_ORDER.indexOf(x.side) - SIDE_ORDER.indexOf(y.side) || x.from - y.from);
}

/**
 * The side of map `a` a seam runs along, south or east, and that side's length.
 *
 * @param a Map `a`.
 * @param b Map `b`.
 * @returns The side and its length, or null when `b` lies neither south nor east of `a` along a stretch.
 */
export function seamSide(a: MapDocument, b: MapDocument): { readonly side: "south" | "east"; readonly length: number } | null {
  const touch = touching(a, b);
  if (touch?.side === "south") {
    return { side: "south", length: a.width };
  }
  return touch?.side === "east" ? { side: "east", length: a.height } : null;
}

/**
 * A crossing's span as one map of its seam numbers it: map `a` reads it as stored, map `b` shifted by the
 * offset between the two maps along the shared edge.
 *
 * @param world The world.
 * @param seam The seam.
 * @param crossing One of its crossings.
 * @param mapId `a` or `b`.
 * @returns The span on that map's side, or null when the crossing has no position yet, the seam's maps share
 *   no edge, or the map is neither of the pair.
 */
export function crossingSpanFor(world: WorldDocument, seam: Seam, crossing: SeamCrossing, mapId: string): CrossingSpan | null {
  const a = world.maps.find((m) => m.id === seam.a);
  const b = world.maps.find((m) => m.id === seam.b);
  if (a?.place === undefined || b?.place === undefined || crossing.from === undefined || crossing.to === undefined) {
    return null;
  }
  const along = seamSide(a, b);
  if (along === null) {
    return null;
  }
  if (mapId === a.id) {
    return { side: along.side, from: crossing.from, to: crossing.to };
  }
  if (mapId !== b.id) {
    return null;
  }
  const shift = sideStart(a.place, along.side) - sideStart(b.place, along.side);
  return { side: along.side === "south" ? "north" : "west", from: crossing.from + shift, to: crossing.to + shift };
}

/**
 * The seams of a world that name a map.
 *
 * @param world The world.
 * @param mapId The map.
 * @returns The seams, in key order.
 */
export function seamsOf(world: WorldDocument, mapId: string): Seam[] {
  return (world.seams ?? []).filter((seam) => seam.a === mapId || seam.b === mapId);
}
